import { Dtos, formatAddress } from "@inrev/common";
import { ContactAddressField, ContactNameFields } from "@portal/components/features/contact-forms";
import {
	FormComponent,
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
} from "@portal/components/form";
import { FormCheckboxOption, FormInput, FormSSN, FormYesNo } from "@portal/components/form/fields";
import { YesNoInput } from "@portal/components/form/inputs/YesNoInput";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { createFormResolver } from "@portal/components/multiform";
import { Button } from "@portal/components/ui/Button";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo, useState } from "react";
import { type FieldError, type UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { type AddressFormFieldControl, useAddressValidation } from "../../../../../api";
import { getSchemaDefaults } from "../../../../../utils/form";
import { type OwnerFormData, type SpouseFormData } from "../schema";

const wasPreviouslySubmitted = (
	data: Partial<OwnerFormData> | Partial<SpouseFormData> | undefined,
): boolean => {
	if (!data) return false;
	return !!(data.firstName && data.lastName);
};

type AccountIndividualModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSave: (owner: OwnerFormData, spouse: OwnerFormData | SpouseFormData | null) => void;
	initialOwner?: Partial<OwnerFormData>;
	initialSpouse?: Partial<SpouseFormData>;
	initialScreen?: "owner" | "spouse";
	showCreditCheck?: boolean;
	partialRequiresSubmit?: boolean;
};

export const AccountIndividualModal = ({
	isOpen,
	onClose,
	onSave,
	initialOwner,
	initialSpouse,
	initialScreen,
	showCreditCheck,
	partialRequiresSubmit,
}: AccountIndividualModalProps) => {
	if (!isOpen) return null;

	return (
		<AccountIndividualModalContent
			onClose={onClose}
			onSave={onSave}
			initialOwner={initialOwner}
			initialSpouse={initialSpouse}
			initialScreen={initialScreen}
			showCreditCheck={showCreditCheck}
			partialRequiresSubmit={partialRequiresSubmit}
		/>
	);
};

const AccountIndividualModalContent = ({
	onClose,
	onSave,
	initialOwner,
	initialSpouse,
	initialScreen = "owner",
	showCreditCheck = false,
	partialRequiresSubmit = true,
}: Omit<AccountIndividualModalProps, "isOpen">) => {
	const [screen, setScreen] = useState<"owner" | "spouse">(initialScreen);
	const [wasOwnerPreviouslySubmitted] = useState(wasPreviouslySubmitted(initialOwner));
	const [wasSpousePreviouslySubmitted] = useState(wasPreviouslySubmitted(initialSpouse));

	const ownerPartialRequiresSubmit = partialRequiresSubmit && !wasOwnerPreviouslySubmitted;
	const spousePartialRequiresSubmit = partialRequiresSubmit && !wasSpousePreviouslySubmitted;

	const ownerFormSchema = useMemo(() => {
		const base = showCreditCheck
			? Dtos.Individual.Create.Request.ownerSchema
			: Dtos.Individual.Create.Request.ownerSchema.extend({
					creditReportPermission: z.boolean().nullish(),
				});
		return base.extend({
			married: z.boolean().nullish().meta({ partial: true }),
		});
	}, [showCreditCheck]);

	const spouseFormSchema = useMemo(() => {
		// Follow owner pattern for base schema selection
		const baseOwnerSchema = showCreditCheck
			? Dtos.Individual.Create.Request.ownerSchema
			: Dtos.Individual.Create.Request.ownerSchema.extend({
					creditReportPermission: z.boolean().nullish(),
				});

		// Extend with spouse-specific fields
		const base = baseOwnerSchema.extend({
			type: z.literal("owner").nullish(),
			creditReportPermission: z.boolean().nullish(), // Override to enable refinement
		});

		// Refine to check ownership type when credit check is enabled
		if (showCreditCheck) {
			return base.refine(
				(data) => {
					if (data.type === "owner") {
						return (
							data.creditReportPermission !== null &&
							data.creditReportPermission !== undefined &&
							data.creditReportPermission !== false
						);
					}
					return true;
				},
				{
					message: "Credit report permission is required",
					path: ["creditReportPermission"],
				},
			);
		}

		return base;
	}, [showCreditCheck]);

	const ownerForm = useForm<OwnerFormData & { married?: boolean | null }>({
		mode: "onBlur",
		resolver: createFormResolver(ownerFormSchema),
		defaultValues: {
			...getSchemaDefaults(ownerFormSchema),
			address: undefined,
		},
	});

	const spouseForm = useForm<SpouseFormData>({
		mode: "onBlur",
		resolver: createFormResolver(spouseFormSchema),
		defaultValues: {
			...getSchemaDefaults(spouseFormSchema),
			address: undefined,
		},
	});

	useEffect(() => {
		if (initialOwner) {
			ownerForm.reset(
				{
					...getSchemaDefaults(ownerFormSchema),
					address: undefined,
					...initialOwner,
					married: initialSpouse !== undefined ? true : undefined,
				},
				{ keepDefaultValues: true },
			);
		}
		if (initialSpouse) {
			spouseForm.reset(
				{
					...getSchemaDefaults(spouseFormSchema),
					address: undefined,
					...initialSpouse,
				},
				{ keepDefaultValues: true },
			);
		}
	}, []);

	const spouseType = spouseForm.watch("type");
	const spouseIsOwner = spouseType === "owner";
	const setSpouseIsOwner = (v: boolean) => {
		spouseForm.setValue("type", v ? "owner" : null);
	};

	const married = ownerForm.watch("married");

	useEffect(() => {
		if (
			(married === undefined || married === null) &&
			Boolean(ownerForm.getValues("firstName")) &&
			!initialSpouse
		) {
			ownerForm.setValue("married", false);
		}
	}, []);

	const [ownerAddressString, setOwnerAddressString] = useState(
		initialOwner?.address ? formatAddress(initialOwner.address) : "",
	);
	const [spouseAddressString, setSpouseAddressString] = useState(
		initialSpouse?.address ? formatAddress(initialSpouse.address) : "",
	);

	const { validatedAddress: ownerValidatedAddress, addressFieldControl: ownerAddressControl } =
		useAddressValidation({
			value: ownerAddressString,
			error: ownerForm.formState.errors.address as FieldError | undefined,
			setValue: (value: string) => setOwnerAddressString(value),
			setError: (error: FieldError) => ownerForm.setError("address", error),
			clearError: () => ownerForm.clearErrors("address"),
		});

	const { validatedAddress: spouseValidatedAddress, addressFieldControl: spouseAddressControl } =
		useAddressValidation({
			value: spouseAddressString,
			error: spouseForm.formState.errors.address as FieldError | undefined,
			setValue: (value: string) => setSpouseAddressString(value),
			setError: (error: FieldError) => spouseForm.setError("address", error),
			clearError: () => spouseForm.clearErrors("address"),
		});

	useEffect(() => {
		if (ownerValidatedAddress) {
			ownerForm.setValue("address", ownerValidatedAddress);
		}
	}, [ownerValidatedAddress]);

	useEffect(() => {
		if (spouseValidatedAddress) {
			spouseForm.setValue("address", spouseValidatedAddress);
		}
	}, [spouseValidatedAddress]);

	useEffect(() => {
		if (married && screen === "spouse") {
			const ownerLastName = ownerForm.getValues("lastName");
			const ownerAddress = ownerForm.getValues("address");

			if (!spouseForm.getValues("lastName") && ownerLastName) {
				spouseForm.setValue("lastName", ownerLastName);
			}
			if (!spouseForm.getValues("address") && ownerAddress) {
				spouseForm.setValue("address", ownerAddress);
				setSpouseAddressString(formatAddress(ownerAddress));
			}
		}
	}, [married, screen]);

	const proceedOwner = () => {
		if (married) {
			setScreen("spouse");
		} else {
			onSave(ownerForm.getValues(), null);
		}
	};

	const handleOwnerNext = () => {
		ownerForm.handleSubmit(proceedOwner)();
	};

	const proceedSpouseSave = () => {
		const owner = ownerForm.getValues();
		const spouse = spouseForm.getValues();
		onSave(owner, spouse);
	};

	const handleSpouseSave = () => {
		spouseForm.handleSubmit(proceedSpouseSave)();
	};

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={screen === "owner" ? "Individual Owner" : "Spouse"}
				onClose={onClose}
			>
				<div className="max-w-[820px] min-w-[747px] px-[40px] pt-[25px] pb-[35px]">
					<div className={screen === "owner" ? "" : "hidden"}>
						<FormSchemaProvider value={ownerFormSchema}>
							<OwnerScreen
								form={ownerForm}
								addressControl={ownerAddressControl}
								onNext={handleOwnerNext}
								showCreditCheck={showCreditCheck}
								partialRequiresSubmit={ownerPartialRequiresSubmit}
							/>
						</FormSchemaProvider>
					</div>
					<div className={screen === "spouse" ? "" : "hidden"}>
						<FormSchemaProvider value={spouseFormSchema}>
							<SpouseScreen
								form={spouseForm}
								addressControl={spouseAddressControl}
								spouseIsOwner={spouseIsOwner}
								setSpouseIsOwner={setSpouseIsOwner}
								onBack={() => setScreen("owner")}
								onSave={handleSpouseSave}
								showCreditCheck={showCreditCheck && spouseIsOwner}
								partialRequiresSubmit={spousePartialRequiresSubmit}
							/>
						</FormSchemaProvider>
					</div>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};

// ─── Owner Screen ───────────────────────────────────────────────────────────

type OwnerScreenProps = {
	form: UseFormReturn<OwnerFormData & { married?: boolean | null }, any, any>;
	addressControl: AddressFormFieldControl;
	onNext: () => void;
	partialRequiresSubmit: boolean;
	showCreditCheck: boolean;
};

const OwnerScreen = ({
	form,
	addressControl,
	onNext,
	partialRequiresSubmit,
	showCreditCheck,
}: OwnerScreenProps) => {
	const married = form.watch("married");

	return (
		<form className="w-full h-fit flex flex-col space-y-[25px]">
			<FormFieldGroup>
				<ContactNameFields control={form.control} />
				<ContactAddressField
					formMethods={form}
					addressControl={addressControl}
					partialRequiresSubmit={partialRequiresSubmit}
				/>
				<FormRow>
					<FormComponent
						formMethods={form}
						name="email"
						label="Email"
						inline
						className="flex-1"
						partialRequiresSubmit={partialRequiresSubmit}
					>
						<FormInput name="email" control={form.control} />
					</FormComponent>
					<FormComponent
						formMethods={form}
						name="ssn"
						label="SSN"
						inline
						className="max-w-[200px]"
						partialRequiresSubmit={partialRequiresSubmit}
					>
						<FormSSN name="ssn" control={form.control} />
					</FormComponent>
				</FormRow>
				<FormRow>
					<FormComponent
						formMethods={form}
						name="ownsAHome"
						label="Does this individual own a home?"
						inline
						className="flex-1"
						partialRequiresSubmit={partialRequiresSubmit}
					>
						<FormYesNo name="ownsAHome" control={form.control} />
					</FormComponent>
					<FormComponent
						formMethods={form}
						name="married"
						label="Is this individual married?"
						inline
						className="flex-1"
						partialRequiresSubmit={partialRequiresSubmit}
					>
						<FormYesNo name="married" control={form.control} />
					</FormComponent>
				</FormRow>
				{showCreditCheck && (
					<FormRow>
						<FormField className="flex-1">
							<FormCheckboxOption
								name="creditReportPermission"
								control={form.control}
								label="This individual authorizes inRev to obtain personal credit report information and other information from Equifax."
								labelClassName="leading-[22px] text-wrap"
							/>
						</FormField>
					</FormRow>
				)}
			</FormFieldGroup>
			<div className="w-full flex justify-center mt-[5px]">
				<Button onClick={onNext} color="primary" filled className="w-[150px] h-[36px]">
					{married ? "Next" : "Save"}
				</Button>
			</div>
		</form>
	);
};

// ─── Spouse Screen ──────────────────────────────────────────────────────────

type SpouseScreenProps = {
	form: UseFormReturn<SpouseFormData, any, any>;
	addressControl: AddressFormFieldControl;
	spouseIsOwner: boolean;
	setSpouseIsOwner: (v: boolean) => void;
	onBack: () => void;
	onSave: () => void;
	partialRequiresSubmit: boolean;
	showCreditCheck: boolean;
};

const SpouseScreen = ({
	form,
	addressControl,
	spouseIsOwner,
	setSpouseIsOwner,
	onBack,
	onSave,
	partialRequiresSubmit,
	showCreditCheck,
}: SpouseScreenProps) => (
	<form className="w-full h-fit flex flex-col space-y-[25px]">
		<FormFieldGroup>
			<FormField>
				<FormItemLabel>Is the spouse also an owner of the company?</FormItemLabel>
				<YesNoInput value={spouseIsOwner} onChange={(v) => setSpouseIsOwner(v === true)} />
			</FormField>
			<ContactNameFields control={form.control} />
			<ContactAddressField
				formMethods={form}
				addressControl={addressControl}
				partialRequiresSubmit={partialRequiresSubmit}
			/>
			<FormRow>
				<FormComponent
					formMethods={form}
					name="email"
					label="Email"
					inline
					className="flex-1"
					partialRequiresSubmit={partialRequiresSubmit}
				>
					<FormInput name="email" control={form.control} />
				</FormComponent>
				{spouseIsOwner && (
					<FormComponent
						formMethods={form}
						name={"ssn" as any}
						label="SSN"
						inline
						className="max-w-[200px]"
						partialRequiresSubmit={partialRequiresSubmit}
					>
						<FormSSN name={"ssn" as any} control={form.control} />
					</FormComponent>
				)}
			</FormRow>
			{showCreditCheck && spouseIsOwner && (
				<FormRow>
					<FormField className="flex-1">
						<FormCheckboxOption
							name="creditReportPermission"
							control={form.control}
							label="This individual authorizes inRev to obtain personal credit report information and other information from Equifax."
							labelClassName="leading-[22px] text-wrap"
						/>
					</FormField>
				</FormRow>
			)}
		</FormFieldGroup>
		<div className="w-full flex justify-center mt-[5px] gap-[10px]">
			<Button onClick={onBack} color="secondary" className="w-[150px] h-[36px]">
				Back
			</Button>
			<Button onClick={onSave} color="primary" filled className="w-[150px] h-[36px]">
				Save
			</Button>
		</div>
	</form>
);
